import Intercom, { boot, onUnreadCountChange, shutdown, update } from '@intercom/messenger-js-sdk';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import usePermissions from '../hooks/auth/usePermissions';
import usePlgOnboarding from '../hooks/auth/usePlgOnboarding';
import useIntercomVisible from '../hooks/utility/useIntercomVisible';
import usePlatformAgentState from '../hooks/context/usePlatformAgentState';
import { setIntercomUnreadCount } from '../services/intercom/intercomUnread';

// The platform agent's companion launcher (PlatformAgentIntercomLauncher) replaces Intercom's own while Amigo shows.
export const INTERCOM_COMPANION_SELECTOR = '#platform-agent-intercom';
// Above a bar fixed to the page's bottom, the bottom-right buttons sit this far over it (design: 12 px).
const ABOVE_BOTTOM_BAR = 12;
const INTERCOM_DEFAULT_PADDING = 20;

// Module-level, not refs: InitialDataProvider swaps its children for a loading
// screen on every company load, so the widget remounts constantly while the
// Intercom session itself lives on the page. Shutting down on unmount would
// flash the launcher on each restaurant switch.
const session = { loaded: false, booted: false, listening: false };

/** Test-only: forget the page-level session between isolated renders. */
export function resetIntercomSession() {
  session.loaded = false;
  session.booted = false;
  session.listening = false;
}

export default function IntercomWidget() {
  const { company } = useSelector((state) => state.currentCompany);
  const { profile } = useSelector((state) => state.initialData);

  const { primaryRole, hasGlobalAccess } = usePermissions();
  const isPlgPartner = usePlgOnboarding();

  // Who gets it is the provider's `messenger_audience`: everyone under
  // FoodAmigos, only the admins of a white-label partner (for the FIN agent),
  // nobody under a legacy reseller. See intercomVisibility.
  const visible = useIntercomVisible();

  // While Ask Amigo shows, our companion button is the launcher; everyone else keeps Intercom's own. On a page with
  // a bar fixed to its bottom (the onboarding wizard), Intercom's own launcher goes above it, like Ask Amigo.
  const { launcher: platformAgentRow } = usePlatformAgentState();
  const companion = visible && platformAgentRow;
  const bottomBar = useSelector((state) => state.platformAgent?.bottomBar ?? 0);

  // `usePlgOnboarding` is tenant-shaped, so it is also true for FoodAmigos staff
  // acting inside a partner tenant. Those are not partners and stay out of the
  // segment — the same global-access staff bypass HasMasterProviderGuard
  // applies.
  const isPartner = isPlgPartner && !hasGlobalAccess;

  const profileProps = {
    user_id: typeof profile?.id === 'string' ? profile.id.trim() : null,
    // Partner staff and admins often have no restaurant selected, which used to
    // read as "Name (undefined)" in Intercom.
    name: company?.name ? `${profile?.name} (${company.name})` : profile?.name,
    email: profile?.email,
    created_at: moment(profile?.created_at ?? undefined).unix(),
    company: company?.name,
    // Single value, not the full `profile.roles` list: Intercom custom
    // attributes cannot hold an array, and audience segments filter on
    // equality. Re-sent on every initialise, so a role change lands with the
    // user's next session.
    user_role: primaryRole,
    // The role alone cannot carry this: a partner's own admin holds plain
    // `admin`, indistinguishable from a FoodAmigos admin. Partner is the tenant,
    // so segments targeting partners filter on this, not on user_role.
    is_partner: isPartner,
  };

  // Always sent, never left out: an update keeps whatever it doesn't name, so a launcher hidden or lifted once would
  // stay so after Amigo goes (another restaurant) or the bar does (leaving the wizard). 20 px is Intercom's default.
  const launcherProps = {
    hide_default_launcher: companion,
    ...(companion ? { custom_launcher_selector: INTERCOM_COMPANION_SELECTOR } : {}),
    vertical_padding: !companion && bottomBar > 0 ? bottomBar + ABOVE_BOTTOM_BAR : INTERCOM_DEFAULT_PADDING,
  };

  const props = {
    app_id: import.meta.env.VITE_INTERCOM_APP_ID,
    ...(profile?.id ? profileProps : {}),
    ...launcherProps,
  };

  // Visibility follows the restaurant in context, so it can flip within a
  // session (switching restaurants, logging out). The SDK itself never takes
  // the launcher down, hence the explicit shutdown; after one, `boot` brings it
  // back. `Intercom()` is only called once visible, so the script is never
  // loaded — and no contact identified — for a session that never qualifies.
  const propsKey = JSON.stringify(props);

  useEffect(() => {
    if (!visible) {
      if (session.booted) {
        shutdown();
        session.booted = false;
        // A shut-down Messenger may drop its callbacks: listen again after the next boot.
        session.listening = false;
      }

      return;
    }

    if (!session.loaded) {
      Intercom(props);
      session.loaded = true;
    } else if (!session.booted) {
      boot(props);
    } else {
      update(props);
    }

    session.booted = true;

    // The companion launcher shows Intercom's unread count; one listener for the page, the SDK has no way to remove it.
    if (!session.listening) {
      onUnreadCountChange(setIntercomUnreadCount);
      session.listening = true;
    }
    // Keyed on the serialized payload, not the object rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, propsKey]);

  return null;
}
